import { useCallback } from 'react';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

interface Params {
	inputSource: 'base' | 'quote';
	setInputSource: (inputSource: 'base' | 'quote') => void;
	quoteSizeStringValue: string;
	onBaseSizeChange: (value: string) => void;
	onQuoteSizeChange: (value: string) => void;
	onInputSourceChange?: (inputSource: 'base' | 'quote') => void;
	setShowErrorMessage: (showErrorMessage: boolean) => void;
	baseSizeStringValue: string;
}

export default function useSizeInputHandlers({
	inputSource,
	setInputSource,
	onBaseSizeChange,
	onQuoteSizeChange,
	onInputSourceChange,
	setShowErrorMessage,
	baseSizeStringValue,
}: Params) {
	const onBaseSizeChangeRef = useSyncedRef(onBaseSizeChange);
	const onQuoteSizeChangeRef = useSyncedRef(onQuoteSizeChange);
	const onInputSourceChangeRef = useSyncedRef(onInputSourceChange);

	const handleChangeInputSource = useCallback(() => {
		const newInputSource = inputSource === 'base' ? 'quote' : 'base';

		setInputSource(newInputSource);

		onInputSourceChangeRef.current?.(newInputSource);

		// If base size has been set to `0`, clear both inputs on switch,
		// which prevents unnecessarily formatting `0` to step size or `0.00`.
		if (!parseFloat(baseSizeStringValue)) {
			onBaseSizeChangeRef.current('');
			onQuoteSizeChangeRef.current('');
		}
	}, [inputSource, setInputSource, baseSizeStringValue]);

	const handleChangeSize = useCallback(
		(value: string) => {
			if (inputSource === 'base') {
				onBaseSizeChangeRef.current(value);
			} else {
				onQuoteSizeChangeRef.current(value);
			}
		},
		[inputSource]
	);

	const handleFocus = useCallback(() => {
		// We don't show the error message while the user is typing, as they may be
		// in the process of typing a valid number (e.g. 0 -> 0. -> 0.0 -> 0.01).
		setShowErrorMessage(false);
	}, [setShowErrorMessage]);

	const handleBlur = useCallback(() => {
		setShowErrorMessage(true);
	}, [setShowErrorMessage]);

	return {
		handleChangeInputSource,
		handleChangeSize,
		handleBlur,
		handleFocus,
	};
}
